import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { PermissionGroup, PermissionGroupLabel } from "./GroupsProvider";

type Props = {
  group?: PermissionGroup | null;
  parentGroup?: PermissionGroup | null;
  preselectedType?: PermissionGroupLabel | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: GroupFormData) => Promise<void>;
  availableParents: PermissionGroup[];
};

export type GroupFormData = {
  name: string;
  label: PermissionGroupLabel;
  parentId: string | null;
};

// Valid parent labels for each child label
const VALID_PARENT_LABELS: Record<PermissionGroupLabel, PermissionGroupLabel[]> = {
  tenant: [],
  state: ["tenant"],
  site: ["tenant", "state"],
  tag: ["tenant", "state", "site", "tag"],
};

// Get default label based on parent
const getDefaultLabel = (parentLabel: PermissionGroupLabel | null): PermissionGroupLabel => {
  if (!parentLabel) return "tenant";
  switch (parentLabel) {
    case "tenant":
      return "state";
    case "state":
      return "site";
    case "site":
      return "tag";
    case "tag":
      return "tag";
    default:
      return "site";
  }
};

const TYPE_DISPLAY_NAMES: Record<PermissionGroupLabel, string> = {
  tenant: "Tenant",
  state: "State",
  site: "Site",
  tag: "Group",
};

export const GroupFormDialog = ({
  group,
  parentGroup,
  preselectedType,
  isOpen,
  onClose,
  onSubmit,
  availableParents,
}: Props) => {
  const isEditing = !!group;

  // Determine the label: preselected > editing > default from parent
  const getInitialLabel = (): PermissionGroupLabel => {
    if (group?.label) return group.label;
    if (preselectedType) return preselectedType;
    return getDefaultLabel(parentGroup?.label ?? null);
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    reset,
    watch,
    setValue,
  } = useForm<GroupFormData>({
    defaultValues: {
      name: group?.name ?? "",
      label: getInitialLabel(),
      parentId: group?.parentId ?? parentGroup?.id ?? null,
    },
  });

  const selectedLabel = watch("label");

  // Reset form when dialog opens with new data
  useEffect(() => {
    if (isOpen) {
      const label = group?.label ?? preselectedType ?? getDefaultLabel(parentGroup?.label ?? null);
      reset({
        name: group?.name ?? "",
        label,
        parentId: group?.parentId ?? parentGroup?.id ?? null,
      });
    }
  }, [isOpen, group, parentGroup, preselectedType, reset]);

  // Filter available parents based on selected label
  const validParentLabels = VALID_PARENT_LABELS[selectedLabel];
  const filteredParents = availableParents.filter((p) => validParentLabels.includes(p.label));

  // When label changes, reset parentId if current parent is invalid
  useEffect(() => {
    const currentParentId = watch("parentId");
    if (currentParentId) {
      const currentParent = availableParents.find((p) => p.id === currentParentId);
      if (currentParent && !validParentLabels.includes(currentParent.label)) {
        setValue("parentId", null);
      }
    }
  }, [selectedLabel, availableParents, validParentLabels, watch, setValue]);

  const handleFormSubmit = async (data: GroupFormData) => {
    try {
      await onSubmit(data);
      reset();
      onClose();
    } catch (err: any) {
      setError("root", { message: err.message });
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // Whether type/parent were pre-selected (from popover flow)
  const hasPreselection = !isEditing && (preselectedType || parentGroup);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Reset showAdvanced when dialog closes/opens
  useEffect(() => {
    if (isOpen) {
      setShowAdvanced(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Get display name for current parent
  const currentParentId = watch("parentId");
  const currentParent = availableParents.find((p) => p.id === currentParentId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />

      {/* Dialog */}
      <div className="relative bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-semibold text-white mb-1">
          {isEditing ? "Edit Group" : `New ${TYPE_DISPLAY_NAMES[selectedLabel]}`}
        </h2>
        {hasPreselection && currentParent && (
          <p className="text-sm text-zinc-400 mb-4">
            Adding to <span className="text-zinc-300">{currentParent.name}</span>
          </p>
        )}
        {!hasPreselection && !isEditing && <div className="mb-4" />}

        <form onSubmit={handleSubmit(handleFormSubmit)}>
          {errors.root && (
            <div className="p-3 mb-4 bg-red-500/20 border border-red-500/50 rounded text-red-300 text-sm">
              {errors.root.message}
            </div>
          )}

          {/* Name - Primary input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-300 mb-1">Name</label>
            <input
              type="text"
              {...register("name", { required: "Name is required" })}
              autoFocus
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={`e.g., ${selectedLabel === "state" ? "Queensland, New South Wales" : selectedLabel === "site" ? "Brisbane CBD, Sydney Office" : selectedLabel === "tag" ? "VIP Venues, Premium Clients" : "My Organisation"}`}
            />
            {errors.name && <p className="mt-1 text-sm text-red-400">{errors.name.message}</p>}
          </div>

          {/* Advanced options - collapsed by default when pre-selected */}
          {hasPreselection && !showAdvanced ? (
            <button
              type="button"
              onClick={() => setShowAdvanced(true)}
              className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-400 transition-colors mb-4"
            >
              <ChevronRight size={14} />
              Change type or parent
            </button>
          ) : (
            <>
              {hasPreselection && (
                <button
                  type="button"
                  onClick={() => setShowAdvanced(false)}
                  className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-400 transition-colors mb-3"
                >
                  <ChevronDown size={14} />
                  Hide options
                </button>
              )}

              {/* Type selector */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-zinc-400 mb-1">Type</label>
                <select
                  {...register("label", { required: "Type is required" })}
                  className="w-full px-2 py-1.5 text-sm bg-zinc-800 border border-zinc-700 rounded text-zinc-300 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  disabled={isEditing && group?.label === "tenant"}
                >
                  <option value="tenant">Tenant (Organisation)</option>
                  <option value="state">State (Region)</option>
                  <option value="site">Site (Location)</option>
                  <option value="tag">Group</option>
                </select>
                {errors.label && <p className="mt-1 text-xs text-red-400">{errors.label.message}</p>}
              </div>

              {/* Parent selector */}
              {selectedLabel !== "tenant" && (
                <div className="mb-4">
                  <label className="block text-xs font-medium text-zinc-400 mb-1">Parent Group</label>
                  <select
                    {...register("parentId", {
                      required: "Parent is required",
                    })}
                    className="w-full px-2 py-1.5 text-sm bg-zinc-800 border border-zinc-700 rounded text-zinc-300 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select parent...</option>
                    {filteredParents.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.label})
                      </option>
                    ))}
                  </select>
                  {errors.parentId && <p className="mt-1 text-xs text-red-400">{errors.parentId.message}</p>}
                </div>
              )}
            </>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Saving..." : isEditing ? "Save Changes" : `Create ${TYPE_DISPLAY_NAMES[selectedLabel]}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
